import { useCallback } from 'react'

import { useAuthedFetch } from '../../auth/useAuthedFetch'
import withFetchCancel from '../../libs/withFetchCancel'
import { useLocale } from '../../i18n/LocaleProvider'
import { useUserContext } from '../../auth/UserProvider'
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from 'react-query'
import {
  transformFiltersToRpcShippingsBody,
  parseFullTrackingResponse,
  parseShippingDetailResponse,
  parseShippingSearchResponse,
  parseShippingsTrackingResponse,
  transformFiltersToBodyFile,
} from './business'

const BACKEND_ENDPOINT = process.env.REACT_APP_BACKEND_ENDPOINT
// Endpoint único que sustituye a los antiguos POST /rpc-shippings y
// GET .../item-history-api/private/v1/history(?shipping_codes=...|/{code}).
const RPC_SHIPPINGS_ENDPOINT = `${BACKEND_ENDPOINT}/item-history-api/private/v2/rpc-shippings`

const SHIPPINGS_TRACKING_KEY = 'shippingsTracking'

const useShippingsTracking = ({
  filters,
  type = 'shippings',
  statusList,
  config = {},
  allowCancelled,
  getAllPages,
  noCount,
  centres,
}) => {
  if (
    type !== 'shippings' &&
    type !== 'incidents' &&
    type !== 'returns' &&
    type !== 'unscheduledPickups'
  )
    throw new Error(`Programming error: Unknown tracking type "${type}"`)

  const authedFetch = useAuthedFetch()
  const { locale } = useLocale()
  // El endpoint espera `client_code` informado con el código de cliente
  // OFUSCADO (el id INTERNO / userData.client_id, expuesto como `clientId`).
  // Se resuelve aquí, no en los filtros de UI/URL, porque es parte de la
  // identidad del usuario logado y no algo que el usuario filtre.
  const { clientId, isMaster } = useUserContext()
  const filtersWithClientCode = { ...filters, clientCode: clientId }

  // NOTE: la clave de la query de seguimiento de envíos o incidencias
  // tiene que estar formada por los filtros que atañen a cada tipo de
  // envío (envío normal o con incidencias) junto con el resto de filtros
  // comunes que el usuario puede aplicar.
  const commonFiltersKeyFragment = {
    search: filters.search,
    centres: filters.centres,
    country: filters.country,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    destinPostalCode: filters.destinPostalCode,
    originPostalCode: filters.originPostalCode,
    postalCodeFrom: filters.postalCodeFrom,
    postalCodeTo: filters.postalCodeTo,
    serviceType: filters.serviceType,
    clientCode: filters.clientCode,
    clientCenterCode: filters.clientCenterCode,
    hasIncident: filters.hasIncident,
    // getOneShippingsPage/transformFiltersToRpcShippingsBody reciben `filters`
    // tal cual y aplican statusCode para cualquier `type` (no solo
    // 'shippings'), así que tiene que estar en la parte común de la key: si no,
    // al aplicar/quitar el filtro de estado estando en otra pestaña (p.ej.
    // Incidencias), la query no se considera stale y no se vuelve a pedir.
    statusCode: filters.statusCode,
  }
  const specificFiltersKeyFragment =
    type === 'shippings'
      ? {
          shippingsPage: filters.shippingsPage,
          shippingsRowsPerPage: filters.shippingsRowsPerPage,
          shippingsSortColumn: filters.shippingsSortColumn,
          shippingsSortType: filters.shippingsSortType,
          shippingCode: filters.shippingCode,
        }
      : type === 'incidents'
      ? {
          incidentsPage: filters.incidentsPage,
          incidentsRowsPerPage: filters.incidentsRowsPerPage,
          incidentsSortColumn: filters.incidentsSortColumn,
          incidentsSortType: filters.incidentsSortType,
          incidentCode: filters.incidentCode,
        }
      : type === 'returns'
      ? {
          returnsPage: filters.returnsPage,
          returnsRowsPerPage: filters.returnsRowsPerPage,
          returnsSortColumn: filters.returnsSortColumn,
          returnsSortType: filters.returnsSortType,
          shippingCode: filters.shippingCode,
        }
      : type === 'unscheduledPickups'
      ? 'unscheduledPickups'
      : null

  // `centres` no forma parte de los filtros de la URL pero sí afecta al body
  // de la petición: transformFiltersToRpcShippingsBody lo usa para obtener el
  // código de centro ofuscado (centre.hideId), que es el que espera el
  // endpoint. Se incluye en la queryKey para que, al recargar la página con
  // `centres` aún vacío (sin cargar desde useUserContext), React Query vuelva
  // a pedir los datos cuando la lista termine de cargar.
  const centreCodesKeyFragment = (centres ?? []).map(centre => centre.hideId)

  const queryKey = [
    SHIPPINGS_TRACKING_KEY,
    type,
    commonFiltersKeyFragment,
    specificFiltersKeyFragment,
    centreCodesKeyFragment,
    // Igual que `centres`, `clientId` llega de forma asíncrona desde los datos
    // iniciales del usuario: si no formara parte de la clave, la query lanzada
    // antes de tenerlo no se repetiría al cargarse.
    clientId,
    locale,
    isMaster,
  ]

  const query = useQuery(
    queryKey,
    async () =>
      getAllPages
        ? await getAllShippingsPages(authedFetch, filtersWithClientCode, allowCancelled, noCount, locale, centres, isMaster)
        : await getOneShippingsPage(authedFetch, filtersWithClientCode, allowCancelled, noCount, locale, centres, isMaster),
    {
      select: ({ data }) =>
        parseShippingsTrackingResponse(data, statusList),
      ...config,
    },
  )

  return query
}

const getOneShippingsPage = async (authedFetch, filters, allowCancelled, noCount, locale, centres, isMaster) => {

  const body = transformFiltersToRpcShippingsBody(filters, allowCancelled, noCount, locale, centres, isMaster)

  if (filters?.centres?.length) {
    return await withFetchCancel(signal =>
      authedFetch(
        'POST',
        RPC_SHIPPINGS_ENDPOINT,
        { data: body, parseJSON: true, signal },
      ),
    )
  } else {
    return {
      data: {
        data: [],
        pagination: {
          page_limit: 0,
          record_count: 0,
          page_offset: {
            current: 0,
            next: -1,
            last: 0,
          },
        },
      },
    }
  }
}

const getAllShippingsPages = async (authedFetch, filters, allowCancelled, noCount, locale, centres, isMaster) => {
  let currentResponse
  const allResponses = {
    data: [],
    pagination: { page_offset: { current: 0, last: 1 } },
  }
  while (
    allResponses.pagination.page_offset.current <
    allResponses.pagination.page_offset.last
  ) {
    const newFilters = {
      ...filters,
      page: allResponses.pagination?.page_offset.current,
    }
    currentResponse = await getOneShippingsPage(
      authedFetch,
      newFilters,
      allowCancelled,
      noCount,
      locale,
      centres,
      isMaster,
    )
    allResponses.data = [...allResponses.data, ...currentResponse.data.data]
    allResponses.pagination = currentResponse.data.pagination
  }
  return { data: allResponses, res: currentResponse }
}


const useInfiniteShippingsTracking = (
  filters,
  statusList,
  config = {},
  extraKey,
  centres,
) => {
  const authedFetch = useAuthedFetch()
  const { locale } = useLocale()
  // Ver la nota de useShippingsTracking: `client_code` viaja con el código de
  // cliente OFUSCADO (clientId), resuelto desde la identidad del usuario.
  const { clientId, isMaster } = useUserContext()

  const fetchShippings = useCallback(
    async ({ pageParam = 1 }) => {
      const body = transformFiltersToRpcShippingsBody(
        { ...filters, clientCode: clientId },
        undefined,
        undefined,
        locale,
        centres,
        isMaster,
      )

      return await withFetchCancel(signal =>
        authedFetch(
          'POST',
          RPC_SHIPPINGS_ENDPOINT,
          {
            data: body,
            parseJSON: true,
            signal,
          },
        ),
      )
    },
    [authedFetch, filters, clientId, locale, centres, isMaster],
  )

  const query = useInfiniteQuery({
    queryKey: [
      SHIPPINGS_TRACKING_KEY,
      extraKey,
      filters,
      (centres ?? []).map(centre => centre.hideId),
      clientId,
      locale,
      isMaster,
    ],
    queryFn: fetchShippings,
    select: data => ({
      pages: data.pages.flatMap(x => x),
      pageParams: data.pageParams,
    }),
    getNextPageParam: (lastPage = {}) => {
      if (!lastPage?.data) return undefined
      const { current, last, next } = lastPage.data.pagination.page_offset
      if (current === last) return undefined
      return next
    },
    ...config,
  })

  return query
}

const useFullTracking = (shippingCode, config = {}) => {
  const authedFetch = useAuthedFetch()
  const { locale } = useLocale()
  // Ver la nota de useShippingsTracking: `client_code` viaja con el código de
  // cliente OFUSCADO (clientId), resuelto desde la identidad del usuario.
  // `centres`/`isMaster`: misma condición que useShippingDetail/seguimiento -
  // se repite aquí porque este hook también se usa de forma independiente
  // (ver ReturnDrawer/ActivateReturn), no siempre detrás de useShippingDetail.
  const { clientId, centres, isMaster } = useUserContext()
  const query = useQuery(
    [
      'simplifiedTracking',
      shippingCode,
      locale,
      clientId,
      isMaster,
      (centres ?? []).map(centre => centre.hideId),
    ],
    () =>
      withFetchCancel(signal =>
        authedFetch(
          'POST',
          RPC_SHIPPINGS_ENDPOINT,
          {
            data: {
              page: { page_offset: 0, page_limit: 1 },
              config: { view: 'WEBCLIENT', language: (locale ?? 'es').toUpperCase() },
              filter: {
                group_operation: 'AND',
                filters: [
                  { field: 'shipping_code', operation: 'EQUALS', value: shippingCode },
                  ...(clientId === undefined || clientId === null
                    ? []
                    : [
                      { field: 'client_code', operation: 'EQUALS', value: clientId },
                    ]),
                  ...(!isMaster && centres?.length
                    ? [
                      {
                        field: 'client_center_code',
                        operation: 'IN',
                        value: centres.map(centre => centre.hideId),
                      },
                    ]
                    : []),
                ],
              },
            },
            parseJSON: true,
            signal,
          },
        ),
      ),
    {
      // TODO: validar contra test que filtrar por shipping_code EQUALS con
      // page_limit:1 siempre devuelve exactamente un item con el array
      // `events` completo (no truncado). Sustituye a la vieja
      // GET .../item-history-api/private/v1/history/{shippingCode}.
      select: ({ data }) => parseFullTrackingResponse(data?.data?.[0]),
      useErrorBoundary: false,
      enabled: shippingCode !== undefined && shippingCode !== null,
      ...config,
    },
  )

  return query
}

const useShippingSearchByCode = (shippingCode, statusList, config = {}) => {
  const authedFetch = useAuthedFetch()

  const query = useQuery(
    ['shippingSearch', { shippingCode }],
    () =>
      withFetchCancel(signal =>
        authedFetch(
          'GET',
          `${BACKEND_ENDPOINT}/shippings/${shippingCode}?mapping_table_code=WEBCLIENT`,
          {
            parseJSON: true,
            signal,
            throwOnError: false,
          },
        ),
      ),
    {
      select: ({ data }) => parseShippingSearchResponse(data, statusList),
      enabled: shippingCode !== '',
      ...config,
    },
  )

  return query
}

const useShippingCancelMutation = options => {
  const authedFetch = useAuthedFetch()
  const queryClient = useQueryClient()

  return useMutation(
    shippingCode =>
      withFetchCancel(signal =>
        authedFetch(
          'POST',
          `${BACKEND_ENDPOINT}/rpc-cancel-shipping-by-shipping-code/${shippingCode}`,
          {
            parseJSON: true,
            signal,
            data: {
              metadata: {
                interchange_id: null,
                timestamp: null,
                correlation_id: null,
                source: null,
              },
              audit: {
                process_code: 'BROKER',
              },
            },
          },
        ),
      ),
    {
      ...options,
      onSettled: (...params) => {
        queryClient.invalidateQueries(SHIPPINGS_TRACKING_KEY)
      },
    },
  )
}

// El detalle de envío usa el mismo endpoint unificado que useFullTracking,
// filtrando por shipping_code (== shippingId, ver ShippingsOverview/View.jsx),
// en vez de la vieja GET /shippings/{id}. Como el item ya trae tracking_info/
// is_unscheduled, parseShipping los rellena aquí directamente y
// mergeTrackingInfoIntoShipping (ShippingDetail.jsx) queda como no-op.
const useShippingDetail = (shippingId, statusList, config = {}) => {
  const authedFetch = useAuthedFetch()
  const { locale } = useLocale()
  // Ver la nota de useShippingsTracking: `client_code` viaja con el código de
  // cliente OFUSCADO (clientId), resuelto desde la identidad del usuario.
  // `centres`/`isMaster`: misma condición que transformFiltersToRpcShippingsBody
  // (seguimiento) - los usuarios master ven envíos de todos los centros del
  // cliente por defecto, así que el filtro de centros no se aplica para ellos.
  const { clientId, centres, isMaster } = useUserContext()

  const query = useQuery(
    [
      'shippingDetail',
      shippingId,
      locale,
      clientId,
      isMaster,
      (centres ?? []).map(centre => centre.hideId),
    ],
    () =>
      withFetchCancel(signal =>
        authedFetch(
          'POST',
          RPC_SHIPPINGS_ENDPOINT,
          {
            data: {
              page: { page_offset: 0, page_limit: 1 },
              config: {
                view: 'WEBCLIENT',
                language: (locale ?? 'es').toUpperCase(),
              },
              filter: {
                group_operation: 'AND',
                filters: [
                  {
                    field: 'shipping_code',
                    operation: 'EQUALS',
                    value: shippingId,
                  },
                  ...(clientId === undefined || clientId === null
                    ? []
                    : [
                      {
                        field: 'client_code',
                        operation: 'EQUALS',
                        value: clientId,
                      },
                    ]),
                  ...(!isMaster && centres?.length
                    ? [
                      {
                        field: 'client_center_code',
                        operation: 'IN',
                        value: centres.map(centre => centre.hideId),
                      },
                    ]
                    : []),
                ],
              },
            },
            parseJSON: true,
            signal,
          },
        ),
      ),
    {
      select: ({ data }) => parseShippingDetailResponse(data?.data?.[0], statusList),
      enabled: shippingId !== undefined && shippingId !== null,
      ...config,
    },
  )

  return query
}
const useFetchShippingItemsForDuplication = () => {
  const authedFetch = useAuthedFetch()
  const { locale } = useLocale()
  const { clientId, centres, isMaster } = useUserContext()

  return useCallback(
    shippingCode =>
      withFetchCancel(signal =>
        authedFetch('POST', RPC_SHIPPINGS_ENDPOINT, {
          data: {
            page: { page_offset: 0, page_limit: 15 },
            config: {
              view: 'WEBCLIENT',
              language: (locale ?? 'es').toUpperCase(),
            },
            filter: {
              group_operation: 'AND',
              filters: [
                {
                  field: 'shipping_code',
                  operation: 'EQUALS',
                  value: shippingCode,
                },
                ...(clientId === undefined || clientId === null
                  ? []
                  : [
                    {
                      field: 'client_code',
                      operation: 'EQUALS',
                      value: clientId,
                    },
                  ]),
                ...(!isMaster && centres?.length
                  ? [
                    {
                      field: 'client_center_code',
                      operation: 'IN',
                      value: centres.map(centre => centre.hideId),
                    },
                  ]
                  : []),
              ],
            },
          },
          parseJSON: true,
          signal,
        }),
      ).then(({ data }) => data?.data ?? []),
    [authedFetch, locale, clientId, isMaster, centres],
  )
}

const useShippingFile = (filters, enabled) => {
  const authedFetch = useAuthedFetch()
  const body = transformFiltersToBodyFile(filters)
  const query = useQuery(
    ['shippingFile'],
    () =>
      withFetchCancel(signal =>
        authedFetch(
          'POST',
          `${BACKEND_ENDPOINT}/cls/query-to-file/v1.0/rpc-file`,
          {
            data: body,
            parseJSON: true,
            signal,
          },
        ),
      ),
    {
      select: ({ data }) => data,
      enabled: !!enabled,
      useErrorBoundary: false,
    },
  )

  return query
}

const useInvalidateShippingsDetail = () => {
  const queryClient = useQueryClient()
  return shippingId =>
    queryClient.invalidateQueries(['shippingDetail', shippingId])
}

// Invalida de una vez las cuatro queries de la pantalla de seguimiento
// (envíos, incidencias, devoluciones y recogidas sin programar), ya que todas
// comparten SHIPPINGS_TRACKING_KEY como primer elemento de su clave.
const useInvalidateShippingsTracking = () => {
  const queryClient = useQueryClient()
  return useCallback(
    () => queryClient.invalidateQueries(SHIPPINGS_TRACKING_KEY),
    [queryClient],
  )
}

export {
  useShippingsTracking,
  useInfiniteShippingsTracking,
  useFullTracking,
  useShippingSearchByCode,
  useShippingCancelMutation,
  useShippingDetail,
  useFetchShippingItemsForDuplication,
  useInvalidateShippingsDetail,
  useInvalidateShippingsTracking,
  useShippingFile
}
